import {
  ALL_HIGHLIGHT_NAMES,
  ALL_HOVER_HIGHLIGHT_NAMES,
  hoverHighlightNameOf,
  pressedHighlightNameOf
} from '../constants';
import {isHighlightApiSupported} from './annotationUtils';

type RegionPaint = Map<string, ReadonlyMap<string, Range[]>>;

/**
 * Sole owner of `CSS.highlights`. The names are document-global, so every
 * write merges all regions' ranges; per region, last write wins. The hover
 * shade is a layer of its own, so a repaint of the marks never wipes it.
 */
const rangesByRegionId: RegionPaint = new Map();
const hoverRangesByRegionId: RegionPaint = new Map();

// Equal priorities paint in registry order, which every delete-then-set reshuffles.
const HOVER_PRIORITY = 1;

const paintLayer = (names: string[], layer: RegionPaint, priority = 0): void => {
  if (!isHighlightApiSupported()) {
    return;
  }
  names.forEach((name) => {
    const merged: Range[] = [];
    layer.forEach((regionRanges) => {
      merged.push(...(regionRanges.get(name) ?? []));
    });
    if (merged.length === 0) {
      CSS.highlights.delete(name);
    } else {
      const highlight = new Highlight(...merged);
      highlight.priority = priority;
      CSS.highlights.set(name, highlight);
    }
  });
};

function rebuildHighlights(): void {
  paintLayer(ALL_HIGHLIGHT_NAMES, rangesByRegionId);
}

const rebuildHoverHighlights = (): void => {
  paintLayer(ALL_HOVER_HIGHLIGHT_NAMES, hoverRangesByRegionId, HOVER_PRIORITY);
};

/** Replaces `regionId`'s painted ranges; a map with no ranges clears the region. */
export function setRegionHighlights(
  regionId: string,
  rangesByName: ReadonlyMap<string, Range[]>
): void {
  if (rangesByName.size === 0) {
    clearRegionHighlights(regionId);
    return;
  }
  rangesByRegionId.set(regionId, rangesByName);
  rebuildHighlights();
}

/** True while `regionId` has painted ranges registered. */
export function hasRegionHighlights(regionId: string): boolean {
  return rangesByRegionId.has(regionId);
}

export function clearRegionHighlights(regionId: string): void {
  // Observers clear on every mutation batch; only the first has anything to erase.
  if (!rangesByRegionId.delete(regionId)) {
    return;
  }
  rebuildHighlights();
}

/** Stops painting `regionId`'s hover shade. */
export const clearRegionHoverHighlights = (regionId: string): void => {
  if (hoverRangesByRegionId.delete(regionId)) {
    rebuildHoverHighlights();
  }
};

/**
 * Paints `regionId`'s hover shade, or its pressed shade, over the marks in `rangesByName`,
 * which is keyed by the marks' own highlight names (as `resolveAnnotationRanges` returns
 * them). One region's shade never clears another's, so a nested region keeps its own.
 */
export const setRegionHoverHighlights = (
  regionId: string,
  rangesByName: ReadonlyMap<string, Range[]>,
  isPressed = false
): void => {
  if (rangesByName.size === 0) {
    clearRegionHoverHighlights(regionId);
    return;
  }
  const shadeNameOf = isPressed ? pressedHighlightNameOf : hoverHighlightNameOf;
  hoverRangesByRegionId.set(
    regionId,
    new Map(Array.from(rangesByName, ([name, ranges]) => [shadeNameOf(name), ranges]))
  );
  rebuildHoverHighlights();
};

export function resetPaintCoordinator(): void {
  rangesByRegionId.clear();
  hoverRangesByRegionId.clear();
  rebuildHighlights();
  rebuildHoverHighlights();
}
